'use client';

import { useSyncExternalStore } from 'react';
import { KEYS_MODE_COOKIE, KEYS_MODE_ON, isKeysModeValue } from './keys-mode';

/**
 * Client-side access to "view translation keys" mode.
 *
 * Keys mode is a cookie overlay (see `keys-mode.ts`) that `request.ts` reads on
 * the server to swap every `t()` string for its dotted key. Static UI strings
 * are handled there automatically — but DB-driven labels (repair destination
 * names, …) never pass through `t()`, so a component that renders them must opt
 * in and render the underlying translation key itself. This hook is that
 * opt-in: read `useKeysMode()` and substitute the key when it's `true`.
 *
 * The cookie is mirrored in a module-level external store read via
 * `useSyncExternalStore` — the server snapshot is always `false` so the initial
 * client render matches the SSR markup, and `setKeysMode` notifies every
 * subscriber (switcher + any label consumer) from a single source of truth.
 */

function readKeysCookie(): boolean {
  if (typeof document === 'undefined') return false;

  // Split rather than build a RegExp from an interpolated name: a cookie name
  // spliced into a pattern is a regex-injection sink (CWE-185), and a cookie
  // value can never contain "; " anyway, so the split is exactly equivalent.
  const prefix = `${KEYS_MODE_COOKIE}=`;
  const entry = document.cookie
    .split('; ')
    .find((candidate) => candidate.startsWith(prefix));

  return isKeysModeValue(
    entry ? decodeURIComponent(entry.slice(prefix.length)) : null
  );
}

let keysModeSnapshot = false;
let initialized = false;
const listeners = new Set<() => void>();

function ensureInitialized(): void {
  if (!initialized && typeof document !== 'undefined') {
    keysModeSnapshot = readKeysCookie();
    initialized = true;
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  ensureInitialized();
  return keysModeSnapshot;
}

function getServerSnapshot(): boolean {
  return false;
}

/** Persist the keys-mode cookie and notify every subscriber optimistically. */
export function setKeysMode(on: boolean): void {
  document.cookie = on
    ? `${KEYS_MODE_COOKIE}=${KEYS_MODE_ON}; path=/; max-age=31536000; samesite=lax`
    : `${KEYS_MODE_COOKIE}=; path=/; max-age=0; samesite=lax`;
  keysModeSnapshot = on;
  initialized = true;
  for (const listener of listeners) {
    listener();
  }
}

/** Reactive read of whether keys mode is currently active. */
export function useKeysMode(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
